import {
  MAX_QUALITY_IDS, ceilingFor, type Ceiling, type MaxQuality, type Rendition, type VariantId,
} from '@multiview/shared';
import { platform } from '../platform.ts';

/**
 * Per-device playback preferences.
 *
 * Kept in localStorage rather than on the server on purpose: the right ceiling
 * is a property of the screen in front of you. A desktop decodes four 1080p50
 * streams without effort, a projector's built-in Google TV board struggles with
 * two, and one account drives both.
 */

const MAX_QUALITY_KEY = 'multiview.maxQuality';
const AUDIO_ANALYSIS_KEY = 'multiview.audioAnalysis';
const MATCH_REFRESH_KEY = 'multiview.matchRefresh';

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch { /* storage unavailable: the choice lasts for this page load */ }
}

/* ------------------------------------------------------------ max quality */

let maxQuality: MaxQuality = (() => {
  const stored = read(MAX_QUALITY_KEY);
  return (MAX_QUALITY_IDS as readonly string[]).includes(stored ?? '') ? (stored as MaxQuality) : 'auto';
})();

export function getMaxQuality(): MaxQuality {
  return maxQuality;
}

export function setMaxQuality(value: MaxQuality): void {
  maxQuality = value;
  write(MAX_QUALITY_KEY, value);
  for (const listener of listeners) listener();
}

const listeners = new Set<() => void>();

/** Called whenever the ceiling setting changes, so playing tiles follow at once. */
export function onMaxQualityChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * What 'auto' allows with two or more full-size pictures.
 *
 * On a TV or phone: 720p at the full frame rate. Two 1080p50 pictures are
 * 207 megapixels per second to decode and composite; two 720p50 are 92, which
 * a projector's TV board still manages, while 50 fps keeps sport fluid. On a
 * 1080p screen split in half, each picture is shown at 960 pixels wide anyway,
 * so 720p loses next to nothing there. Desktops get the source.
 */
export const AUTO_MULTI: Ceiling = platform === 'desktop' ? 'source' : '720p50';

/** The ceiling for full-size slots right now; see ceilingFor. */
export function effectiveCeiling(setting: MaxQuality, bigSlots: number, floor: number): Ceiling {
  return ceilingFor(setting, bigSlots, floor, AUTO_MULTI);
}

/** The rendition a full-size slot asks the proxy for under a ceiling. */
export function fullRendition(ceiling: Ceiling): Rendition {
  return ceiling === 'source' ? 'main' : ceiling;
}

/**
 * The variant to announce with every request. Taken for two big slots, the
 * case 'auto' reduces for: a session started while one picture is shown then
 * already encodes what the split screen will need, and switching layouts does
 * not rebuild a running stream.
 */
let hint: VariantId | null = null;

export function setVariantHint(value: VariantId | null): void {
  hint = value;
}

export function variantHint(): VariantId | null {
  return hint;
}

export function hintFor(setting: MaxQuality, floor: number): VariantId | null {
  const ceiling = effectiveCeiling(setting, 2, floor);
  return ceiling === 'source' ? null : ceiling;
}

/* --------------------------------------------------------- audio analysis */

/**
 * Whether the cheer detection listens at all. On by default everywhere: the
 * desktop routes tile audio through WebAudio, TVs and phones use the levels
 * the proxy measures (serverLevels.ts) -- the in-browser ways made a Google TV
 * stutter, or heard silence there.
 */
export function audioAnalysisPreferred(): boolean {
  const stored = read(AUDIO_ANALYSIS_KEY);
  if (stored === 'on') return true;
  if (stored === 'off') return false;
  return true;
}

export function setAudioAnalysisPreferred(on: boolean): void {
  write(AUDIO_ANALYSIS_KEY, on ? 'on' : 'off');
}

/* ------------------------------------------------------ refresh matching */

/**
 * Whether the TV app should switch the display to the content's frame rate.
 * On by default: European broadcast is 50 fps, Google TV boxes leave the HDMI
 * output at 60 Hz, and 50 frames on 60 refreshes means every fifth frame is
 * shown twice -- a regular judder that looks exactly like a decoder struggling.
 */
export function matchRefreshPreferred(): boolean {
  return read(MATCH_REFRESH_KEY) !== 'off';
}

export function setMatchRefreshPreferred(on: boolean): void {
  write(MATCH_REFRESH_KEY, on ? 'on' : 'off');
}
